<template>
  <div class="car_list_item" :class="{ compact }" @click="search">
    <div class="image_box">
      <img :src="firstPhoto" @error="imageLoadOnError" />
    </div>
    <div class="info_box">
      <h2>{{ car_data["highlights"] }}</h2>
      <div class="badges">
        <span class="badge badge_year" v-if="car_data['year']">{{ car_data["year"] }}</span>
        <template v-if="car_data['engine_type'] != null">
          <span
            class="badge"
            v-for="box in String(car_data['engine_type']).split(',')"
            :key="box"
          >{{ box }}cc</span>
        </template>
        <span class="badge" v-if="car_data['transmission']">{{ car_data["transmission"] }}</span>
        <span class="badge" v-if="car_data['fuel'] != null">{{ car_data["fuel"].toUpperCase() }}</span>
        <span class="badge" v-if="car_data['car_keys'] == 'yes'">🔑</span>
      </div>
      <div class="details">
        <div class="detail_item">
          <span class="detail_label">Przebieg</span>
          <span class="detail_value">
            {{ Math.round(Number(car_data["odometer"]) * 1.6093).toLocaleString() }} km
          </span>
        </div>
        <div class="detail_item">
          <span class="detail_label">Lokalizacja</span>
          <span class="detail_value">{{ car_data["location"] }}</span>
        </div>
        <div class="detail_item" v-if="car_data['lot_number']">
          <span class="detail_label">Numer Lot</span>
          <span class="detail_value">{{ car_data["lot_number"] }}</span>
        </div>
        <div class="detail_item" v-if="car_data['vin']">
          <span class="detail_label">Numer VIN</span>
          <span class="detail_value mono">{{ car_data["vin"] }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Katalog amerykański (Copart / IAAI) — dane z /get-car-vin/ w formacie US.
// Aukcje japońskie mają własny komponent: JdmCarListItem.vue.
import { goToResults } from "@/utils/embed";

const PLACEHOLDER = "https://ralfvanveen.com/wp-content/uploads/2021/06/Placeholder-_-Glossary.svg";

export default {
  name: "CarListItem",
  props: ["car_data", "compact"],
  computed: {
    firstPhoto() {
      const p = this.car_data?.photos;
      return Array.isArray(p) && p.length ? p[0] : PLACEHOLDER;
    },
  },
  methods: {
    imageLoadOnError(e) {
      e.target.src = PLACEHOLDER;
    },
    search() {
      // Otwieramy aukcję, zachowując aktywne filtry, aby powrót do wyników je
      // odtworzył. Parametr `lot_number` zostaje dla zgodności z WordPressem.
      // W osadzeniu w iframe nawigujemy stroną nadrzędną, natywnie — routerem.
      goToResults(this.$router, {
        ...this.$route.query,
        lot_number: this.car_data["id"],
      });
    },
  },
};
</script>

<style scoped>
.car_list_item {
  background: #111;
  border: 1px solid rgba(255, 255, 255, 0.07);
  cursor: pointer;
  display: flex;
  gap: 0;
  align-items: stretch;
  width: 100%;
  box-sizing: border-box;
  transition: border-color 0.18s, background 0.18s;
  font-family: 'DM Sans', sans-serif;
}

.car_list_item:hover {
  background: #161616;
  border-color: rgba(255, 255, 255, 0.14);
}

.image_box {
  flex: 0 0 260px;
  width: 260px;
  aspect-ratio: 4/3;
  background: #1a1a1a;
  overflow: hidden;
  flex-shrink: 0;
}

.image_box img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.3s ease;
}

.car_list_item:hover .image_box img {
  transform: scale(1.03);
}

.info_box {
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1 1 0;
  min-width: 0;
  padding: 22px 24px;
}

h2 {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #fff;
  margin: 0;
  line-height: 1.2;
}

/* Spec badges */
.badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.badge {
  padding: 5px 11px;
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.65);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.badge_year {
  background: rgba(210, 31, 31, 0.15);
  border-color: rgba(210, 31, 31, 0.3);
  color: #d21f1f;
}

/* Detail rows */
.details {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 32px;
  margin-top: auto;
}

.detail_item {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.detail_label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.32);
}

.detail_value {
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.3;
}

.mono {
  font-family: ui-monospace, 'Cascadia Code', monospace;
  font-size: 12px;
  letter-spacing: 0.03em;
}

/* Compact (grid / 3-column) layout: vertical card, image on top */
.car_list_item.compact {
  flex-direction: column;
}

.car_list_item.compact .image_box {
  width: 100%;
  flex: none;
  aspect-ratio: 16/9;
}

.car_list_item.compact .info_box {
  padding: 18px 20px;
  gap: 12px;
}

.car_list_item.compact h2 {
  font-size: 17px;
}

.car_list_item.compact .details {
  grid-template-columns: 1fr 1fr;
  gap: 10px 18px;
}

@media (max-width: 640px) {
  .car_list_item {
    flex-direction: column;
  }

  .image_box {
    width: 100%;
    flex: none;
    aspect-ratio: 16/9;
  }

  .details {
    grid-template-columns: 1fr;
    gap: 10px;
  }
}
</style>
